import classNames from 'classnames'
import { CircularCheckbox } from 'components/CircularCheckbox'
import { SelectionModal } from 'components/layout/SelectionModal'
import { PlanTag } from 'components/planTag'
import TooltipIcon from 'components/TooltipIcon'
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import { isMobile } from 'react-device-detect'
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { PromptGroup, ToneType } from 'service/list'
import { Me } from 'service/user'
import { useScriptStore } from 'store/useScriptStore'
import { FormValues } from 'utils/type'

interface ScriptSelectorProps {
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  control: Control<FormValues, any>
  translatePlanName: (planName: string) => string
  promptGroupList: PromptGroup[] | undefined
  userInfo: Me
  setPromptStatus: Dispatch<SetStateAction<boolean[]>>
  setToneTooltip: Dispatch<SetStateAction<boolean[]>>
  promptStatus: boolean[]
  toneTooltip: boolean[]
}

export const ScriptSelector = ({
  watch,
  setValue,
  control,
  translatePlanName,
  promptGroupList,
  userInfo,
  setPromptStatus,
  setToneTooltip,
  promptStatus,
  toneTooltip,
}: PropsWithChildren<ScriptSelectorProps>) => {
  const { promptName, setPromptName, setToneName, isModalOpen, toggleModal } =
    useScriptStore()

  const promptRefs = useRef<(HTMLOrSVGElement | null)[]>([])
  const tonRefs = useRef<(HTMLOrSVGElement | null)[]>([])
  const [toneGroup, setToneGroup] = useState<ToneType[]>([])
  const [selectedPrompt, setSelectedPrompt] = useState<
    PromptGroup | { planName: string; introduction: string }
  >({
    planName: '',
    introduction: '',
  })
  const [isMobilePromptSelectBoxOpen, setMobilePromptSelectBoxOpen] =
    useState<boolean>(false)

  const handlePromptMouseOver = (index: number) => {
    const updatedPromptState = promptStatus?.map((value, idx) =>
      idx === index ? true : value,
    )

    setPromptStatus(updatedPromptState ?? [])
  }

  const handlePromptMouseLeave = (index: number) => {
    const updatedPromptState = promptStatus?.map((value, idx) =>
      idx === index ? false : value,
    )
    setPromptStatus(updatedPromptState ?? [])
  }

  const handleMouseOver = (index: number) => {
    const updatedToneTooltipState = toneTooltip.map((value, idx) =>
      idx === index ? true : value,
    )

    setToneTooltip(updatedToneTooltipState)
  }

  const handleMouseLeave = (index: number) => {
    const updatedToneTooltipState = toneTooltip.map((value, idx) =>
      idx === index ? false : value,
    )
    setToneTooltip(updatedToneTooltipState)
  }
  const handleSelectPrompt = (prompt: PromptGroup) => {
    // 기존의 프롬프트와 그리고 TONE LIST가 있다
    //1. promptId 변경
    if (prompt && prompt?.usable) {
      setValue('aiPromptTypeId', prompt.id)
      setPromptName(prompt.name)
      setToneGroup(prompt?.toneGroup)
      setToneName(prompt?.toneGroup[0].name)
      setValue('aiToneTypeId', prompt?.toneGroup[0].id)
      setToneTooltip(new Array(prompt?.toneGroup?.length).fill(false))
    }
  }

  const handleMobilePromptSelect = (prompt: PromptGroup) => {
    //1.promptName, planName이 필요합니다.

    if (prompt && prompt?.usable) {
      setSelectedPrompt(prompt)
      setValue('aiPromptTypeId', prompt.id)
      setPromptName(prompt.name)
      setToneGroup(prompt?.toneGroup)
      setToneName(prompt?.toneGroup[0].name)
      setValue('aiToneTypeId', prompt?.toneGroup[0].id)
      setToneTooltip(new Array(prompt?.toneGroup?.length).fill(false))
    }
  }

  useEffect(() => {
    const aiPromptTypeId = watch('aiPromptTypeId')
    const aiToneTypeId = watch('aiToneTypeId')

    if (aiPromptTypeId && promptGroupList) {
      const [initPromptGroup] = promptGroupList.filter(
        (el) => el.id === aiPromptTypeId,
      )

      setPromptName(initPromptGroup.name)
      setSelectedPrompt(initPromptGroup)

      const [intiTone] = initPromptGroup.toneGroup.filter(
        (el) => el.id === aiToneTypeId,
      )

      setToneGroup(initPromptGroup.toneGroup)
      setToneName(intiTone?.name)
    }
  }, [promptGroupList, watch('aiPromptTypeId'), watch('aiToneTypeId')])

  return (
    <SelectionModal isModal={isModalOpen}>
      <div
        className={classNames('', {
          'flex h-auto w-[816px] flex-col gap-8 bg-white px-8 pb-44 pt-8':
            !isMobile,
          'flex w-full flex-col gap-8 overflow-auto bg-white px-4 pt-10':
            isMobile,
        })}
      >
        <div className="flex flex-col gap-4">
          <p className="flex flex-col items-center gap-4 md:items-start">
            <span className="text-left text-[24px] font-bold">
              AI 대본 컨셉 & 말투 선택
            </span>
            <span>상품에 어울리는 대본의 컨셉과 말투를 선택해보세요!</span>
          </p>
        </div>
        <div className="flex flex-col gap-8 pb-40 md:pb-0">
          <div className="ai-prompt flex flex-col items-start">
            <p className="mb-[16px] text-[18px] font-bold">대본 컨셉</p>
            <div className="grid w-full grid-cols-3 rounded-[10px] md:h-[40px] md:bg-[#EEF1F7] md:px-2 md:py-1">
              {!isMobile ? (
                promptGroupList?.map((prompt, index) => {
                  return (
                    <>
                      <Controller
                        render={({ field }) => (
                          <>
                            <div
                              key={`prompt_${index}`}
                              className={classNames(
                                'flex cursor-pointer justify-center gap-1',
                                {
                                  'rounded-[4px] bg-white':
                                    watch('aiPromptTypeId') === prompt.id,
                                },
                              )}
                              onClick={() => {
                                handleSelectPrompt(prompt)
                              }}
                            >
                              <button
                                className={classNames('', {})}
                                type="button"
                                disabled={!prompt!.usable}
                              >
                                {prompt.name}
                              </button>
                              <div className="ml-[2px] mr-[8px] flex h-full items-start pt-1">
                                <PlanTag
                                  planName={translatePlanName(prompt.planName)}
                                  className=""
                                />
                              </div>
                              <TooltipIcon
                                tone={prompt}
                                index={index}
                                tonRefs={promptRefs}
                                toneTooltip={promptStatus}
                                handleMouseOver={handlePromptMouseOver}
                                handleMouseLeave={handlePromptMouseLeave}
                              />
                            </div>
                          </>
                        )}
                        rules={{
                          validate: (value) => {
                            return Number(watch('aiPromptTypeId')) === 0
                              ? 'aiPromptType ID must not be 0'
                              : true
                          },
                        }}
                        name="aiPromptTypeId"
                        control={control}
                      />
                    </>
                  )
                })
              ) : (
                <>
                  <div
                    className="relative flex h-[52px] w-[343px] items-center rounded-[6px] border-[1px] border-[#D4D9DF] px-4"
                    onClick={() => {
                      setMobilePromptSelectBoxOpen((prev) => !prev)
                    }}
                    onMouseLeave={() => {
                      setMobilePromptSelectBoxOpen((prev) => !prev)
                    }}
                  >
                    <div className="flex gap-1">
                      <span className="text-[18px]">{promptName}</span>
                      <div className="ml-[2px] mr-[8px] flex h-full items-start pt-1">
                        <PlanTag
                          planName={translatePlanName(selectedPrompt?.planName)}
                          className=""
                        />
                      </div>
                    </div>
                    <img
                      src="assets/img/chevron-down.png"
                      className={classNames('', {
                        'absolute bottom-2 right-2 h-[16px] w-[16px]':
                          !isMobile,
                        'absolute bottom-4 right-2 h-[16px] w-[16px]': isMobile,
                      })}
                      alt=""
                    />
                    {isMobilePromptSelectBoxOpen && (
                      <div className="border-[#D4D9DF absolute left-0 top-14 z-50 w-full rounded-[6px] border-[1px] bg-white py-3">
                        {promptGroupList?.map((prompt, index) => {
                          return (
                            <>
                              <button
                                className="flex h-[46px] w-full items-center gap-1 px-4 py-2 text-[18px]"
                                type="button"
                                onClick={() => {
                                  handleMobilePromptSelect(prompt)
                                }}
                              >
                                <p className="">{prompt.name}</p>
                                <div className="ml-[2px] mr-[8px] flex h-full items-start pt-1">
                                  <PlanTag
                                    planName={translatePlanName(
                                      prompt.planName,
                                    )}
                                    className=""
                                  />
                                </div>
                              </button>
                            </>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="ai-prompt md:min-h-auto flex flex-col items-start">
            <p className="mb-[16px] text-[18px] font-bold">
              {promptName} 나레이션 말투
            </p>
            <div className="grid grid-cols-1 gap-x-[53.67px] gap-y-[16px] md:grid-cols-2">
              {toneGroup?.map((tone, index) => {
                return (
                  <>
                    <div
                      className="flex w-full justify-between md:items-center"
                      key={`tone_${tone.id}_${index}`}
                    >
                      <div className="flex h-full items-center">
                        <Controller
                          render={({ field }) => (
                            <CircularCheckbox
                              {...field}
                              id={index}
                              checked={watch('aiToneTypeId') === tone.id}
                              disabled={
                                !userInfo ||
                                userInfo?.userType === 'normalUser' ||
                                userInfo?.userType === 'lightUser'
                              }
                              onChange={() => {
                                setValue('aiToneTypeId', tone.id)
                                setToneName(tone.name)
                              }}
                              label={tone.name}
                            />
                          )}
                          name="aiToneTypeId"
                          control={control}
                        />
                        <div className="ml-[2px] mr-[8px] flex h-full items-start pt-1">
                          <PlanTag
                            planName={translatePlanName(tone.planName)}
                            className=""
                          />
                        </div>
                        <TooltipIcon
                          tone={tone}
                          index={index}
                          tonRefs={tonRefs}
                          toneTooltip={toneTooltip}
                          handleMouseOver={handleMouseOver}
                          handleMouseLeave={handleMouseLeave}
                        />
                      </div>
                    </div>
                  </>
                )
              })}
            </div>
          </div>
        </div>
        {isMobile && (
          <div
            className={classNames(
              'z-50 flex w-[335px] flex-col gap-8 bg-white',
              {},
            )}
          >
            <button
              className="z-50 h-[60px] w-full rounded-full bg-main font-bold text-white"
              onClick={toggleModal}
              type="button"
            >
              저장
            </button>
          </div>
        )}
      </div>
      {!isMobile && (
        <div className="sticky bottom-0 z-50 w-full rounded-b-[20px] bg-white p-8 shadow-main before:absolute before:left-0 before:top-[-10px] before:h-[10px] before:w-full before:bg-gradient-to-t before:from-black/10 before:to-transparent before:content-[''] md:w-[816px]">
          <button
            className="z-50 h-[60px] w-full rounded-full bg-main font-bold text-white"
            onClick={toggleModal}
            type="button"
          >
            저장
          </button>
        </div>
      )}
    </SelectionModal>
  )
}
